import { Box, TableCell } from "@mui/material";
import { useParams } from "react-router";
import { DotLegalTable, ITableHeader, ServerSideConfig } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import TableRow from "@common/components/tableRow/TableRow";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import FilterBar from "@common/components/filterBar/FilterBar";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import { useRiskHistoryOverview } from "./RiskHistoryOverview.hooks";
import { useRiskHistoryOverviewStyles } from "./RiskHistoryOverview.styles";
import { allRiskHistoryColumns, RiskHistoryColumn, RiskHistoryEvent, RiskHistoryEventKind } from "./RiskHistoryOverview.types";
import RiskLevelChip from "../RiskLevelChip";
import RiskTrajectory from "../RiskTrajectory";
import RiskAssessmentTimelineDialog from "./riskAssessmentTimelineDialog/RiskAssessmentTimelineDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

const ratingEventKinds = new Set<RiskHistoryEventKind>([
    RiskHistoryEventKind.Assessed,
    RiskHistoryEventKind.Reassessed,
    RiskHistoryEventKind.Reconfirmed,
    RiskHistoryEventKind.MitigationPromoted,
    RiskHistoryEventKind.ScaleMigrated,
    RiskHistoryEventKind.MatrixRebased,
]);

// The forward plan states show the expected residual trajectory (risk authored against -> expected residual)
// in the Change column; the proposal's description is surfaced in the Why column instead, so Change stays
// consistent across the lifecycle. Deferred/Cancelled are excluded — a paused or abandoned plan is not driving
// the risk toward its residual, so their Change column stays blank.
const residualTrajectoryEventKinds = new Set<RiskHistoryEventKind>([
    RiskHistoryEventKind.PlanProposed,
    RiskHistoryEventKind.PlanApproved,
    RiskHistoryEventKind.PlanPlanned,
    RiskHistoryEventKind.PlanStarted,
    RiskHistoryEventKind.PlanReactivated,
    RiskHistoryEventKind.PlanCompleted,
]);

// An event about the organisation as a whole: it belongs to an assessment, unlike an area-level configuration
// change, but carries no entity — and that absence is what says the organisation is the subject.
const isOrganisationSubject = (event: RiskHistoryEvent) => event.riskAssessmentId !== null && event.entityType === null;

function RiskHistoryOverview() {
    const styles = useRiskHistoryOverviewStyles();
    const { translateString, translateDateWithTime } = useTranslation();
    const { riskAreaId } = useParams<{ riskAreaId: string }>();

    const {
        result,
        isLoading,
        pageNumber,
        pageSize,
        onPageChange,
        filterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchText,
        onSearchTextChange,
        getEventLabel,
        getEntityTypeLabel,
        getWhy,
        getRiskColor,
        getRiskIcon,
        selectedEvent,
        openTimeline,
        closeTimeline,
    } = useRiskHistoryOverview(riskAreaId);

    const columnHeader: Record<RiskHistoryColumn, string> = {
        [RiskHistoryColumn.When]: translateString("riskHistoryWhen"),
        [RiskHistoryColumn.Event]: translateString("riskHistoryEvent"),
        [RiskHistoryColumn.Entity]: translateString("entity"),
        [RiskHistoryColumn.Type]: translateString("type"),
        [RiskHistoryColumn.Scenario]: translateString("scenario"),
        [RiskHistoryColumn.Change]: translateString("riskHistoryChange"),
        [RiskHistoryColumn.By]: translateString("riskHistoryBy"),
        [RiskHistoryColumn.Why]: translateString("riskHistoryWhy"),
    };

    const headers: Array<ITableHeader<RiskHistoryEvent>> = allRiskHistoryColumns.map((column) => ({
        text: columnHeader[column],
        property: column,
        notSortable: true,
    }));

    const totalCount = result?.totalCount ?? 0;

    const serverSideConfig: ServerSideConfig = {
        order: "desc",
        orderBy: RiskHistoryColumn.When,
        onSortChange: () => {},
        page: pageNumber,
        from: totalCount > 0 ? (pageNumber - 1) * pageSize + 1 : 0,
        to: Math.min(pageNumber * pageSize, totalCount),
        totalPages: result?.totalPages ?? 1,
        totalCount: totalCount,
        onPageChange: onPageChange,
    };

    // Each side of a delta is labelled and coloured on the frame it was scored under, which the row carries with
    // it — the timeline dialog renders the same way. Without a frame the level cannot be placed on any scale, so
    // the chip is dropped rather than guessed at.
    const renderRiskChip = (level: number | null, levelLabel: string | null, scale: number | null, eliminated = false) => {
        if (level == null || scale == null) return null;
        const label = eliminated ? translateString("riskLevelEliminated") : levelLabel || level.toString();
        return <RiskLevelChip level={level} label={label} color={getRiskColor(level, scale)} icon={getRiskIcon(level, scale)} eliminated={eliminated} />;
    };

    const renderCurrentChip = (event: RiskHistoryEvent, eliminated = false) => renderRiskChip(event.riskLevel, event.riskLevelLabel, event.scale, eliminated);

    const renderPreviousChip = (event: RiskHistoryEvent) => renderRiskChip(event.previousRiskLevel, event.previousRiskLevelLabel, event.previousScale);

    const renderChange = (event: RiskHistoryEvent) => {
        // Rating deltas: previous -> current, or just the current pill on the first assessment.
        if (ratingEventKinds.has(event.eventKind)) {
            if (event.riskLevel == null) return null;
            return event.previousRiskLevel != null ? (
                <RiskTrajectory from={renderPreviousChip(event)} to={renderCurrentChip(event)} />
            ) : (
                <RiskTrajectory from={renderCurrentChip(event)} />
            );
        }

        // Mitigation proposed/approved/completed: the risk the plan was authored against -> its expected residual.
        if (residualTrajectoryEventKinds.has(event.eventKind)) {
            if (event.riskLevel == null) return null;
            return event.previousRiskLevel != null ? (
                <RiskTrajectory from={renderPreviousChip(event)} to={renderCurrentChip(event)} />
            ) : (
                <RiskTrajectory from={renderCurrentChip(event)} />
            );
        }

        // Accept holds the risk flat at its accepted level; avoid sends it to a grey "eliminated" pill.
        if (event.eventKind === RiskHistoryEventKind.RiskAccepted) {
            if (event.riskLevel == null) return null;
            return <RiskTrajectory from={renderCurrentChip(event)} to={renderCurrentChip(event)} />;
        }
        if (event.eventKind === RiskHistoryEventKind.RiskAvoided) {
            if (event.riskLevel == null) return null;
            return <RiskTrajectory from={renderCurrentChip(event)} to={renderCurrentChip(event, true)} />;
        }

        if (event.eventKind === RiskHistoryEventKind.AreaConfigChanged) {
            const lines: Array<string> = [];
            if (event.oldScale != null && event.newScale != null && event.oldScale !== event.newScale)
                lines.push(`${translateString("riskHistoryScale")}: ${event.oldScale} → ${event.newScale}`);
            if (event.oldAppetite != null && event.newAppetite != null && event.oldAppetite !== event.newAppetite)
                lines.push(`${translateString("riskHistoryAppetite")}: ${event.oldAppetite} → ${event.newAppetite}`);
            return <Box>{lines.length > 0 ? lines.join(" · ") : "—"}</Box>;
        }

        return null;
    };

    // The subject's type: one of the assessable entity types, the organisation when an assessment has no
    // entity, and a dash for an area-level event, which is about the area itself rather than any subject.
    const renderType = (event: RiskHistoryEvent) => {
        if (event.entityType !== null) {
            return <EntityTypeIconWithName type={event.entityType} name={getEntityTypeLabel(event.entityType)} />;
        }

        return isOrganisationSubject(event) ? translateString("organization") : "—";
    };

    const renderCell = (column: RiskHistoryColumn, event: RiskHistoryEvent) => {
        switch (column) {
            case RiskHistoryColumn.When:
                return <TableCell key={column}>{translateDateWithTime(new Date(event.eventTime))}</TableCell>;
            case RiskHistoryColumn.Event:
                return (
                    <TableCell key={column} sx={styles.bold}>
                        {getEventLabel(event.eventKind)}
                    </TableCell>
                );
            case RiskHistoryColumn.Entity:
                // The organisation has no entity to name, and the Type column already says so, so the cell stays
                // blank rather than showing the dash that stands for "this event names no subject".
                return <TableCell key={column}>{event.entityName ?? (isOrganisationSubject(event) ? "" : "—")}</TableCell>;
            case RiskHistoryColumn.Type:
                return <TableCell key={column}>{renderType(event)}</TableCell>;
            case RiskHistoryColumn.Scenario:
                return <TableCell key={column}>{event.scenarioName ?? ""}</TableCell>;
            case RiskHistoryColumn.Change:
                return <TableCell key={column}>{renderChange(event)}</TableCell>;
            case RiskHistoryColumn.By:
                return <TableCell key={column}>{event.actorName ?? ""}</TableCell>;
            case RiskHistoryColumn.Why: {
                const why = getWhy(event);
                return (
                    <TableCell key={column}>
                        <Box sx={styles.why}>{why.isHtml ? <MentionInputRender value={why.value} /> : why.value}</Box>
                    </TableCell>
                );
            }
            default:
                return null;
        }
    };

    return (
        <Box sx={styles.container}>
            <TablePagePaper>
                <ListPageToolbar
                    leftContent={
                        <FilterBar
                            isLoading={isLoading}
                            fetchFilterData
                            filterData={filterOptions}
                            onMenuClose={onFilterChange}
                            onRemove={onFilterChange}
                            pinnedFilterIds={pinnedFilterIds}
                            onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                            searchValue={searchText}
                            onSearchChange={onSearchTextChange}
                            searchLabel={translateString("search")}
                        />
                    }
                />

                <DotLegalTable
                    internalScroll
                    headers={headers}
                    defaultOrderBy={"eventTime"}
                    defaultOrder={"desc"}
                    data={result?.items ?? []}
                    isLoading={isLoading}
                    disableFilterAndSortAndSlicing
                    serverSide={serverSideConfig}
                    rowsPerPage={pageSize}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    // A row opens the full timeline of the assessment it belongs to. Area-level events
                    // (AreaConfigChanged) carry no assessment, so they stay non-clickable.
                    clickableRows
                    renderRow={(event, index) => {
                        const canOpenTimeline = event.riskAssessmentId != null;
                        return (
                            <TableRow
                                key={`${event.eventKind}-${event.riskAssessmentId ?? "area"}-${index}`}
                                onClick={canOpenTimeline ? () => openTimeline(event) : undefined}
                                sx={{ cursor: canOpenTimeline ? "pointer" : "default" }}
                            >
                                {allRiskHistoryColumns.map((column) => renderCell(column, event))}
                            </TableRow>
                        );
                    }}
                />
            </TablePagePaper>

            {selectedEvent?.riskAssessmentId && (
                <RiskAssessmentTimelineDialog
                    riskAssessmentId={selectedEvent.riskAssessmentId}
                    entityName={selectedEvent.entityName}
                    scenarioName={selectedEvent.scenarioName}
                    highlightOccurredAt={selectedEvent.eventTime}
                    highlightKind={selectedEvent.eventKind}
                    onClose={closeTimeline}
                />
            )}
        </Box>
    );
}

export default RiskHistoryOverview;
